import { useEffect, useState } from "react";
import { Megaphone, Pencil, Plus, Power, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import MediaPicker from "../MediaPicker";
import CriticalPinDialog from "../CriticalPinDialog";

const TYPE_TR = { image: "Görsel", video: "Video", youtube: "YouTube" };

export default function AdsTab() {
    const [ads, setAds] = useState(null);
    const [cats, setCats] = useState([]);
    const [editor, setEditor] = useState(null);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [pinTarget, setPinTarget] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = () => {
        api.get("/admin/ads").then((r) => setAds(r.data)).catch(() => {});
        api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
    };
    useEffect(() => { load(); }, []);

    const save = async () => {
        if (!editor.title.trim() || !editor.media_url.trim()) return toast.error("Başlık ve medya zorunludur");
        setBusy(true);
        try {
            const payload = { title: editor.title, type: editor.type, media_url: editor.media_url, link: editor.link, placement: editor.placement, sort: Number(editor.sort) || 0 };
            if (editor.ad_id) {
                await api.put(`/admin/ads/${editor.ad_id}`, payload);
                toast.success("Reklam güncellendi");
            } else {
                await api.post("/admin/ads", payload);
                toast.success("Reklam oluşturuldu");
            }
            setEditor(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kaydedilemedi");
        } finally {
            setBusy(false);
        }
    };

    const toggle = async (ad) => {
        await api.post(`/admin/ads/${ad.ad_id}/toggle`);
        toast.success(ad.active ? "Reklam pasifleştirildi" : "Reklam aktifleştirildi");
        load();
    };

    const remove = async (pin) => {
        setBusy(true);
        try {
            await api.delete(`/admin/ads/${pinTarget.ad_id}`, { headers: { "X-Critical-Pin": pin } });
            toast.success("Reklam silindi");
            setPinTarget(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Silinemedi");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="ads-tab">
            <div className="mb-4 flex items-center justify-between">
                <p className="text-sm text-muted-foreground">Ana sayfa ve kategori sayfalarında gösterilecek reklamlar.</p>
                <Button onClick={() => setEditor({ ad_id: null, title: "", type: "image", media_url: "", link: "", placement: "home", sort: 0 })} className="bg-terra-600 hover:bg-terra-700 text-white" data-testid="ad-new-button">
                    <Plus className="mr-2 h-4 w-4" /> Yeni Reklam
                </Button>
            </div>
            {!ads ? (
                <div className="h-48 animate-pulse rounded-2xl bg-muted" />
            ) : (
                <div className="space-y-3">
                    {ads.map((ad) => (
                        <div key={ad.ad_id} className={`flex items-center gap-4 rounded-2xl border bg-card p-4 shadow-sm ${!ad.active ? "opacity-55" : ""}`} data-testid={`ad-row-${ad.ad_id}`}>
                            {ad.type === "image" ? (
                                <img src={ad.media_url} alt="" className="h-14 w-20 rounded-lg object-cover" />
                            ) : (
                                <div className="flex h-14 w-20 items-center justify-center rounded-lg bg-muted"><Megaphone className="h-5 w-5 text-muted-foreground" /></div>
                            )}
                            <div className="flex-1">
                                <p className="font-semibold">{ad.title}</p>
                                <p className="text-xs text-muted-foreground">{TYPE_TR[ad.type]} • {ad.placement === "home" ? "Ana sayfa" : cats.find((c) => c.category_id === ad.placement)?.name || ad.placement}</p>
                            </div>
                            <Badge variant="outline" className={`text-[10px] ${ad.active ? "border-emerald-200 bg-emerald-100 text-emerald-800" : "border-stone-200 bg-stone-100 text-stone-600"}`}>{ad.active ? "Yayında" : "Pasif"}</Badge>
                            <div className="flex gap-2">
                                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setEditor({ ...ad })} data-testid={`ad-edit-${ad.ad_id}`}><Pencil className="h-3 w-3" /></Button>
                                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => toggle(ad)} data-testid={`ad-toggle-${ad.ad_id}`}><Power className="h-3 w-3" /></Button>
                                <Button size="sm" variant="outline" className="h-8 text-xs text-destructive" onClick={() => setPinTarget(ad)} data-testid={`ad-delete-${ad.ad_id}`}><Trash2 className="h-3 w-3" /></Button>
                            </div>
                        </div>
                    ))}
                    {!ads.length && <p className="rounded-2xl border border-dashed p-16 text-center text-sm text-muted-foreground" data-testid="ads-empty">Henüz reklam yok.</p>}
                </div>
            )}

            <Dialog open={!!editor} onOpenChange={() => setEditor(null)}>
                <DialogContent data-testid="ad-editor">
                    {editor && (
                        <>
                            <DialogHeader><DialogTitle className="font-display">{editor.ad_id ? "Reklamı Düzenle" : "Yeni Reklam"}</DialogTitle></DialogHeader>
                            <div className="space-y-3">
                                <Input value={editor.title} onChange={(e) => setEditor({ ...editor, title: e.target.value })} placeholder="Reklam başlığı *" data-testid="ad-title" />
                                <div className="grid grid-cols-2 gap-3">
                                    <select className="h-10 rounded-md border bg-background px-3 text-sm" value={editor.type} onChange={(e) => setEditor({ ...editor, type: e.target.value })} data-testid="ad-type">
                                        <option value="image">Görsel</option>
                                        <option value="video">Video (URL)</option>
                                        <option value="youtube">YouTube</option>
                                    </select>
                                    <select className="h-10 rounded-md border bg-background px-3 text-sm" value={editor.placement} onChange={(e) => setEditor({ ...editor, placement: e.target.value })} data-testid="ad-placement">
                                        <option value="home">Ana Sayfa</option>
                                        {cats.filter((c) => !c.parent_id).map((c) => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
                                    </select>
                                </div>
                                {editor.type === "image" ? (
                                    <div className="flex items-center gap-3 rounded-xl border border-dashed p-3">
                                        {editor.media_url && <img src={editor.media_url} alt="" className="h-16 w-24 rounded-lg object-cover" />}
                                        <Button size="sm" variant="outline" onClick={() => setPickerOpen(true)} data-testid="ad-pick-library">Ortam Kütüphanesi'nden Seç</Button>
                                    </div>
                                ) : (
                                    <Input value={editor.media_url} onChange={(e) => setEditor({ ...editor, media_url: e.target.value })} placeholder={editor.type === "youtube" ? "YouTube bağlantısı (https://youtube.com/watch?v=...)" : "Video URL (mp4)"} data-testid="ad-media-url" />
                                )}
                                <Input value={editor.link} onChange={(e) => setEditor({ ...editor, link: e.target.value })} placeholder="Tıklanınca gidilecek bağlantı (opsiyonel)" data-testid="ad-link" />
                                <Input type="number" value={editor.sort} onChange={(e) => setEditor({ ...editor, sort: e.target.value })} placeholder="Sıralama (küçük önce)" data-testid="ad-sort" />
                            </div>
                            <DialogFooter>
                                <Button variant="outline" onClick={() => setEditor(null)}>Vazgeç</Button>
                                <Button onClick={save} disabled={busy} className="bg-terra-600 hover:bg-terra-700 text-white" data-testid="ad-save">{busy ? "Kaydediliyor..." : "Kaydet"}</Button>
                            </DialogFooter>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            <MediaPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onSelect={(url) => { setEditor((e) => ({ ...e, media_url: url })); setPickerOpen(false); }} />
            <CriticalPinDialog open={!!pinTarget} onClose={() => setPinTarget(null)} onSubmit={remove} actionLabel={pinTarget ? `"${pinTarget.title}" reklamını silme` : ""} busy={busy} />
        </div>
    );
}
